/**
 * AuthContext – JWT-backed authentication for CareArena
 *
 * Uses the FastAPI backend (/auth/login, /auth/register, /auth/me).
 * Token is stored in localStorage and attached to every API request via
 * the axios interceptor in api.js.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  login as apiLogin,
  register as apiRegister,
  logout as apiLogout,
  getMe,
  updateProfile as apiUpdateProfile,
  uploadAvatar as apiUploadAvatar,
} from '../config/api';
import { clearToken, getToken } from '../utils/tokenStore';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Restore user from existing token on mount
    const token = getToken();
    if (token) {
      getMe()
        .then((me) => {
          setUser(me);
          setSession({ access_token: token });
        })
        .catch(() => {
          // Token is invalid or expired — clear it
          clearToken();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  /**
   * Sign in with email + password.
   * Throws an Error with a user-readable message on failure.
   */
  const login = async (email, password, { remember = true } = {}) => {
    const data = await apiLogin(email.trim().toLowerCase(), password, { remember });
    const token = data.access_token;
    setSession({ access_token: token });
    setUser(data.user);
    return data.user;
  };

  /**
   * Create a new account and sign in automatically.
   * Throws an Error with a user-readable message on failure.
   *
   * @param {string} email
   * @param {string} password
   * @param {string} [fullName]
   */
  const signUp = async (email, password, fullName = '') => {
    const data = await apiRegister(email.trim().toLowerCase(), password, fullName);
    const token = data.access_token;
    setSession({ access_token: token });
    setUser(data.user);
    // Mirror the Supabase return shape: { user, session }
    return { user: data.user, session: { access_token: token } };
  };

  /** Sign out the current user. */
  const logout = async () => {
    apiLogout();
    setUser(null);
    setSession(null);
  };

  /** Returns the JWT access token for authenticated API calls. */
  const getAccessToken = () => session?.access_token ?? null;

  /**
   * Save profile fields and fold the result back into context.
   *
   * The server's response is used as the new user rather than the values that
   * were sent: it has trimmed the strings and turned blanks into nulls, so
   * trusting the request would leave the UI showing something the database
   * does not hold.
   */
  const updateProfile = async (fields) => {
    const updated = await apiUpdateProfile(fields);
    setUser(updated);
    return updated;
  };

  /** Upload a new profile picture; resolves to the updated user. */
  const uploadAvatar = async (file) => {
    const updated = await apiUploadAvatar(file);
    setUser(updated);
    return updated;
  };

  const value = {
    user,
    session,
    loading,
    login,
    signUp,
    logout,
    getAccessToken,
    updateProfile,
    uploadAvatar,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
